/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from "react";
import { Link } from "react-router-dom";
import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import breakpoint from "@shared/styles/breakpoints";

interface LargeButtonProps {
  children: React.ReactNode;
  type: "button" | "submit" | "reset" | "internalLink" | "externalLink";
  link?: string;
  onClick?: (event: React.MouseEvent, ...args: unknown[]) => void;
  onMouseDown?: (event: React.MouseEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  styleType?: "largePrimary" | "largeSecondary";
  dangerous?: boolean;
  useBreakpoint?: boolean;
  mediaQuery?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  [key: string]: any; // for additional props
}

const LargeButton: React.FC<LargeButtonProps> = ({
  children,
  type,
  link,
  onClick,
  onMouseDown,
  disabled = false,
  loading = false,
  styleType = "largePrimary",
  dangerous = false,
  useBreakpoint = true,
  mediaQuery = "sm",
  ...props
}) => {
  const handleClick = (event: React.MouseEvent, ...args: unknown[]) => {
    if (!disabled && onClick) {
      onClick(event, ...args);
    } else if (disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleOnMouseDown = (event: React.MouseEvent) => {
    if (onMouseDown) {
      onMouseDown(event);
    }
  };

  const getStyles = (t: any, useBreakpoint: boolean, loading: boolean, mediaQuery: string) => {
    const typeStyles = getTypeStyles(t, t.dark, useBreakpoint, mediaQuery);
    return css`
      ${[typeStyles, t.text.medium, t.rounded.sm]}
      white-space: nowrap;
      border: none;
      appearance: none;
      position: relative;
      cursor: pointer;
      max-width: 100%;

      &:disabled {
        opacity: ${loading ? 1 : 0.5};
        cursor: default;
      }
    `;
  };

  const getTypeStyles = (t: any, dark: boolean, useBreakpoint: boolean, mediaQuery: string) => {
    // Get the media query string from breakpoint
    const mediaQueryString = breakpoint[mediaQuery as keyof typeof breakpoint];

    const buttonShape = css`
      ${[t.h(10), t.text.base, t.px(6)]}
      display: ${type === "internalLink" || type === "externalLink" ? "inline-flex" : "flex"};
      align-items: center;
      justify-content: center;
      outline: none;
      width: 100%;

      ${useBreakpoint
        ? css`
            ${mediaQueryString} {
              ${[t.h(8), t.text.sm]}
              width: auto;
            }
          `
        : ""}
    `;

    switch (styleType) {
      case "largePrimary":
        return css`
          ${buttonShape};
          color: ${t.dark ? t.color.black : t.color.white};
          &:link,
          &:visited {
            color: ${t.dark ? t.color.black : t.color.white};
          }
          background: ${t.dark
            ? "linear-gradient(120deg, #FBD7FF 10%, #FFDEC1 90%)"
            : dangerous
            ? t.color.red[600]
            : t.color.black};
          transition: background 0.2s ease, box-shadow 0.2s ease;
          &:hover:not([disabled]) {
            background: ${t.dark
              ? "linear-gradient(to right, #FBD7FF -30%, #FFDEC1 70%)"
              : dangerous
              ? t.color.red[700]
              : t.color.gray[700]};
          }
          &:focus {
            ${t.dark
              ? `box-shadow: 0 0 0 2px ${t.color.gray[500]}`
              : `box-shadow: 0 0 0 2px ${dangerous ? t.color.gray[300] : t.color.gray[400]}`};
          }

          > span {
            overflow-x: hidden;
            line-height: 1.6;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        `;
      case "largeSecondary":
        return css`
          ${buttonShape}
          background-color: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
          transition: background-color 0.2s ease, box-shadow 0.2s ease;
          color: ${t.dark ? t.color.gray[200] : dangerous ? t.color.red[600] : t.color.black};
          &:link,
          &:visited {
            color: ${t.dark ? t.color.gray[200] : dangerous ? t.color.red[600] : t.color.black};
          }
          border: 1px solid ${t.color.gray[200]};
          &:hover:not([disabled]) {
            color: ${t.dark ? t.color.white : dangerous ? t.color.red[700] : t.color.black};
            background-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
          }
          &:focus {
            box-shadow: 0 0 0 2px ${t.dark ? t.color.gray[500] : t.color.gray[300]};
          }
          &:hover:focus {
            box-shadow: 0 0 0 2px ${t.dark ? t.color.gray[500] : t.color.gray[300]};
          }

          > span {
            overflow-x: hidden;
            line-height: 1.6;
            white-space: nowrap;
            text-overflow: ellipsis;
          }
        `;
      default:
        return buttonShape;
    }
  };

  /* KIND TRANSITION
  ======================================================= */
  switch (type) {
    case "internalLink":
      return (
        <Link
          to={link!}
          {...props}
          onClick={handleClick}
          css={(theme) => getStyles(theme, useBreakpoint, loading, mediaQuery)}
        >
          <span>{children}</span>
        </Link>
      );
    case "externalLink":
      return (
        <a
          href={link}
          target={props.target || "_blank"}
          rel="noopener noreferrer"
          {...props}
          onClick={handleClick}
          css={(theme) => getStyles(theme, useBreakpoint, loading, mediaQuery)}
        >
          <span>{children}</span>
        </a>
      );
    default:
      return (
        <button
          type={type}
          css={(theme) => getStyles(theme, useBreakpoint, loading, mediaQuery)}
          {...props}
          onClick={handleClick}
          onMouseDown={handleOnMouseDown}
          disabled={disabled}
        >
          <span>{children}</span>
          {loading && (
            <Styled.Loader styleType={styleType} data-testid="loading-animation">
              <div />
              <div />
              <div />
            </Styled.Loader>
          )}
        </button>
      );
  }
};

export default LargeButton;

/* Styled Components
======================================================= */
const Styled: any = {};

Styled.Loader = styled.div<{ styleType: string }>((props: any) => {
  const t = props.theme;
  return css`
    label: LargeButton_Loader;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    height: 100%;
    opacity: 100%;
    &:before {
      ${t.rounded.sm}
      background: ${props.styleType === "largePrimary"
        ? t.dark
          ? "linear-gradient(to right, #FBD7FF -40%, #FFDEC1 80%)"
          : t.color.gray[700]
        : t.dark
        ? t.color.gray[600]
        : t.color.gray[300]};
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: block;
      content: "";
    }
    div {
      position: relative;
      width: 0.125rem;
      margin: 0.125rem;
      background-color: ${props.styleType === "largePrimary"
        ? t.dark
          ? t.color.black
          : t.color.white
        : t.dark
        ? t.color.gray[200]
        : t.color.black};
      animation: ${loadingAnimation} 1.6s cubic-bezier(0, 0.5, 0.5, 1) infinite;
    }
    div:nth-of-type(1) {
      animation-delay: -0.24s;
    }
    div:nth-of-type(2) {
      animation-delay: -0.12s;
    }
    div:nth-of-type(3) {
      animation-delay: 0;
    }
  `;
});

const loadingAnimation = keyframes`
  0% {
    height: 0.75rem;
  }
  30%,
  100% {
    height: 0.125rem;
  }
`;
